import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import styles from "./EditExerciseModal.module.scss";
import {
  useLazySearchExercizesQuery,
  useEditExercizeInTrainingMutation,
} from "@/store/trainingsApi";
import { ExercizeObj } from "@/store/trainingsApi";

type Approach = {
  approach_number: number;
  repetitions: number;
  repetitions_margin?: number;
  weight?: string;
};

type EditExerciseModalProps = {
  onClose: () => void;
  initData: string;
  trainingId: number;
  exerciseId: number;
  currentExercise: ExercizeObj;
  currentApproaches: Approach[];
  onSuccess: () => void;
  fromUser?: number;
};

export const EditExerciseModal: React.FC<EditExerciseModalProps> = ({
  onClose,
  initData,
  trainingId,
  exerciseId,
  currentExercise,
  currentApproaches,
  onSuccess,
  fromUser,
}) => {
  const [approaches, setApproaches] = useState<Approach[]>(
    currentApproaches.length > 0
      ? currentApproaches
      : [
          {
            approach_number: 1,
            repetitions: 10,
            weight: "",
          },
        ]
  );

  const [editExercize, { isLoading: isEditing }] = useEditExercizeInTrainingMutation();

  const handleAddApproach = () => {
    setApproaches([
      ...approaches,
      {
        approach_number: approaches.length + 1,
        repetitions: 10,
        weight: "",
      },
    ]);
  };

  const handleRemoveApproach = (index: number) => {
    const updated = approaches.filter((_, i) => i !== index);
    // Перенумеровываем подходы
    updated.forEach((approach, idx) => {
      approach.approach_number = idx + 1;
    });
    setApproaches(updated);
  };

  const handleUpdateApproach = (
    index: number,
    field: keyof Approach,
    value: string | number
  ) => {
    const updated = [...approaches];
    (updated[index][field] as any) = value;
    setApproaches(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (approaches.length === 0) {
      toast.error("Добавьте хотя бы один подход");
      return;
    }

    try {
      await editExercize({
        training_id: trainingId,
        exercize_id: exerciseId,
        body: {
          approaches: approaches.map((approach) => ({
            approach_number: approach.approach_number,
            repetitions: approach.repetitions,
            repetitions_margin: approach.repetitions_margin,
            weight: approach.weight || undefined,
          })),
          fromUser: fromUser,
        },
      }).unwrap();

      toast.success("Упражнение обновлено!");
      onClose();
      onSuccess();
    } catch (error: any) {
      console.error("Ошибка редактирования упражнения:", error);
      toast.error(error?.data?.detail || "Ошибка при редактировании упражнения");
    }
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose}></div>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Редактировать упражнение</h2>
          <button className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.exerciseCard}>
            {currentExercise.title_photo && (
              <img
                src={currentExercise.title_photo}
                alt={currentExercise.title}
                className={styles.exerciseThumb}
              />
            )}
            <div>
              <div className={styles.exerciseName}>{currentExercise.title}</div>
              {currentExercise.muscle_group && (
                <div className={styles.exerciseMuscleGroup}>
                  {currentExercise.muscle_group}
                </div>
              )}
            </div>
          </div>

          <div className={styles.approachesList}>
            <div className={styles.approachesHeader}>
              <span>Подходы</span>
              <button
                type="button"
                className={styles.addApproachBtn}
                onClick={handleAddApproach}
              >
                + Добавить подход
              </button>
            </div>
            {approaches.map((approach, index) => (
              <div key={index} className={styles.approachRow}>
                <span className={styles.approachNumber}>Подход {approach.approach_number}</span>
                <input
                  type="number"
                  min="1"
                  value={approach.repetitions}
                  onChange={(e) =>
                    handleUpdateApproach(index, "repetitions", parseInt(e.target.value) || 0)
                  }
                  placeholder="Повторения"
                  className={styles.approachInput}
                />
                <input
                  type="text"
                  value={approach.weight || ""}
                  onChange={(e) => handleUpdateApproach(index, "weight", e.target.value)}
                  placeholder="Вес (кг)"
                  className={styles.approachInput}
                />
                {approaches.length > 1 && (
                  <button
                    type="button"
                    className={styles.removeApproachBtn}
                    onClick={() => handleRemoveApproach(index)}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Отмена
            </button>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={isEditing}
            >
              {isEditing ? "Сохранение..." : "Сохранить изменения"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

